import React, { useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { Contact } from '../types';
import { apiService } from '../services/api';
import ContactItem from './ContactItem';
import Dialog from './Dialog';

interface QuestionsSectionProps {
  contacts: Contact[];
  clientName: string;
  clientId: string;
}

const QuestionsSection: React.FC<QuestionsSectionProps> = ({ contacts, clientName, clientId }) => {
  const [question, setQuestion] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [dialog, setDialog] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    type: 'success' | 'error' | 'info';
  }>({
    isOpen: false,
    title: '',
    message: '',
    type: 'info'
  });

  const { user, getAccessTokenSilently } = useAuth0();

  const handleSubmit = async () => {
    if (!question.trim()) {
      setDialog({
        isOpen: true,
        title: 'Question Required',
        message: 'Please enter your question before submitting.',
        type: 'error'
      });
      return;
    }

    setIsSubmitting(true);

    try {
      // Get auth token and set it
      const token = await getAccessTokenSilently();
      apiService.setAuthToken(token);

      // Submit the question
      await apiService.submitQuestion({
        clientId,
        question: question.trim(),
        userEmail: user?.email,
        userName: user?.name
      });

      // Clear the form
      setQuestion('');

      // Show success dialog
      setDialog({
        isOpen: true,
        title: 'Question Submitted Successfully',
        message: 'Thank you for your question! We will follow up within 48 hours.',
        type: 'success'
      });
    } catch (error) {
      console.error('Error submitting question:', error);
      setDialog({
        isOpen: true,
        title: 'Submission Failed',
        message: 'Failed to submit your question. Please try again or contact support.',
        type: 'error'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const closeDialog = () => {
    setDialog(prev => ({ ...prev, isOpen: false }));
  };

  return (
    <section className="">
      {/* Questions Section */}
      <div className="bg-[#f6f8fc] rounded-2xl py-10 px-8 shadow-sm text-center">
        <div className="font-bold text-[22px] mb-6 text-[#1d2a4d]">
          Any questions about {clientName} Search AI?
        </div>
        <div className="font-semibold text-gray-900 mb-[18px]">Points of contact</div>
        <div className="flex flex-col gap-3 items-center mb-[18px]">
          {contacts.map((contact) => (
            <ContactItem 
              key={contact.id} 
              contact={contact} 
              variant="card" 
              showRole={true}
            />
          ))}
        </div>
        <div className="flex items-center my-[18px] text-gray-400">
          <div className="flex-1 h-px bg-gray-300"></div>
          <span className="mx-4 font-medium">or</span>
          <div className="flex-1 h-px bg-gray-300"></div>
        </div>
        <div className="font-semibold text-gray-900 mb-2">Questions & Feedback</div>
        <div className="text-gray-500 text-sm mb-4">💬 We'll follow up within 48h.</div>
        <textarea 
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          disabled={isSubmitting}
          className="w-[90%] min-h-[60px] rounded-lg border border-gray-300 p-3 text-[15px] mb-[18px] resize-y bg-white font-satoshi outline-none transition-colors duration-200 focus:border-blue-500"
          placeholder="Your question..."
        />
        <button 
          onClick={handleSubmit}
          disabled={isSubmitting || !question.trim()}
          className={`${
            isSubmitting || !question.trim() 
              ? 'bg-gray-400 cursor-not-allowed' 
              : 'bg-blue-600 hover:bg-blue-700 cursor-pointer'
          } text-white border-none rounded-3xl py-2.5 px-9 font-semibold text-base transition-colors duration-200`}
        >
          {isSubmitting ? 'Sending...' : 'Send'}
        </button>
      </div>
      <div className="text-center text-gray-400 text-sm mt-8 mb-0">
        <span role="img" aria-label="eye">👁️</span> This dashboard is shared with OpenAI
      </div>

      {/* Dialog for feedback */}
      <Dialog
        isOpen={dialog.isOpen}
        onClose={closeDialog}
        title={dialog.title}
        message={dialog.message}
        type={dialog.type}
      />
    </section>
  );
};

export default QuestionsSection; 